/**
 * Buyer API Service - Direct Integration with BuyerController
 *
 * Direct calls to commerce-service/BuyerController endpoints
 * Architecture: Frontend → BuyerController → BuyerService → BuyerRepository → buyers table
 *
 * This is the ONLY service that should be used for customer/buyer operations
 * It directly maps to BuyerController endpoints at /api/v1/customers
 */

import { microservicesClient } from '@/services/microservices';
import { authManager } from '@/services/api';

const COMMERCE_API_BASE = '/api/v1/customers';

/**
 * Address object for buyer registration
 * Maps to backend AddressDto with street, city, state, pincode, country
 */
export interface AddressDto {
  street: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
}

/**
 * Buyer response from backend Buyer entity
 * Maps backend field names to frontend names via @JsonProperty decorators
 */
export interface Buyer {
  id: number;
  externalId: string;
  buyerCode?: string;
  type: 'COMMERCIAL' | 'INDUSTRIAL' | 'RESIDENTIAL'; // Maps from buyerType field
  name: string; // Maps from companyName field
  contactPerson: string; // Maps from contactPersonName field
  phone: string; // Maps from primaryPhone field
  secondaryPhone?: string;
  email: string;
  gstNumber?: string;
  panNumber?: string;
  registeredAddress: string; // Full address string
  creditLimit: number;
  creditUsed: number;
  paymentTerms: string;
  preferredQualityGrade?: string;
  monthlyRequirementCubicMeters?: number;
  contractStatus: string;
  onboardingStatus: string;
  kycCompletionDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp: string;
  path: string;
}

/**
 * Create Buyer Request DTO
 * Matches backend CreateBuyerRequest with field name mappings:
 * - type: buyerType
 * - name: companyName
 * - contactPerson: contactPersonName
 * - phone: primaryPhone
 * - address: AddressDto object (street, city, state, pincode, country)
 * - creditLimit: BigDecimal number
 * - paymentTerms: Integer (1-90 days)
 */
export interface CreateBuyerRequest {
  type: 'COMMERCIAL' | 'INDUSTRIAL' | 'RESIDENTIAL';
  name: string;
  contactPerson: string;
  phone: string;
  email: string;
  gstNumber?: string;
  panNumber?: string;
  address: AddressDto; // Complex object with street, city, state, pincode, country
  creditLimit: number;
  paymentTerms: number; // Must be integer (days)
  contractStatus?: string; // ACTIVE, PENDING, SUSPENDED, TERMINATED
  onboardingStatus?: string; // INITIATED, DOCUMENTS_SUBMITTED, VERIFIED, APPROVED
  salesUnitId?: number; // Assigns customer to a sales unit for data isolation
}

export interface UpdateBuyerRequest {
  type?: 'COMMERCIAL' | 'INDUSTRIAL' | 'RESIDENTIAL';
  name?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  gstNumber?: string;
  panNumber?: string;
  address?: AddressDto;
  creditLimit?: number;
  paymentTerms?: number;
}

/**
 * CREATE BUYER - POST /api/v1/customers
 * Calls BuyerController.createBuyer()
 *
 * @param request Create buyer request matching Buyer entity fields
 * @returns ApiResponse with created Buyer from database
 */
export async function createBuyer(request: CreateBuyerRequest): Promise<ApiResponse<Buyer>> {
  try {
    console.log('📡 [BuyerAPI] Creating buyer via BuyerController');
    console.log('📡 [BuyerAPI] Endpoint: POST /api/v1/customers');
    console.log('📡 [BuyerAPI] Request data:', JSON.stringify(request, null, 2));
    console.log('📡 [BuyerAPI] creditLimit type:', typeof request.creditLimit, 'value:', request.creditLimit);
    console.log('📡 [BuyerAPI] paymentTerms type:', typeof request.paymentTerms, 'value:', request.paymentTerms);

    // Use microservicesClient instead of direct fetch
    const response = await microservicesClient.post('commerce-service', `${COMMERCE_API_BASE}`, request);

    console.log('📡 [BuyerAPI] Response received:', response.data);
    console.log('✅ [BuyerAPI] Buyer created successfully:', response.data.data || response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ [BuyerAPI] Failed to create buyer:', error);
    console.error('❌ [BuyerAPI] Full error:', error);
    throw error;
  }
}

/**
 * GET ALL BUYERS - GET /api/v1/customers
 * Fetches paginated list of buyers from database
 *
 * @param page Page number (0-indexed, default: 0)
 * @param size Page size (default: 100)
 * @param sortBy Sort field (default: companyName)
 * @param sortDir Sort direction asc/desc (default: asc)
 * @returns ApiResponse with paginated buyers
 */
export async function getAllBuyers(params?: {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: string;
}): Promise<ApiResponse<{
  content: Buyer[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}>> {
  try {
    console.log('📡 [BuyerAPI] Fetching all buyers from database');
    console.log('📡 [BuyerAPI] Params:', params);

    const finalParams = params || { page: 0, size: 100, sortBy: 'companyName', sortDir: 'asc' };

    const queryParams: Record<string, any> = {};
    if (finalParams.page !== undefined) queryParams.page = finalParams.page;
    if (finalParams.size !== undefined) queryParams.size = finalParams.size;
    if (finalParams.sortBy) queryParams.sortBy = finalParams.sortBy;
    if (finalParams.sortDir) queryParams.sortDir = finalParams.sortDir;

    console.log('📡 [BuyerAPI] Query params:', queryParams);

    // Use microservicesClient instead of direct fetch
    const response = await microservicesClient.get('commerce-service', `${COMMERCE_API_BASE}`, { params: queryParams });

    console.log('📡 [BuyerAPI] Response received:', response.data);
    console.log('✅ [BuyerAPI] Buyers fetched successfully:', response.data?.data?.content?.length || 0, 'buyers');

    return response.data;
  } catch (error: any) {
    console.error('❌ [BuyerAPI] Failed to fetch buyers:', error.message);
    throw error;
  }
}

/**
 * GET SINGLE BUYER - GET /api/v1/customers/{id}
 * Fetches a single buyer by ID
 *
 * @param id Buyer ID
 * @returns ApiResponse with Buyer data
 */
export async function getBuyerById(id: number): Promise<ApiResponse<Buyer>> {
  try {
    console.log('📡 [BuyerAPI] Fetching buyer by ID:', id);

    const response = await microservicesClient.get<ApiResponse<Buyer>>('commerce-service', `${COMMERCE_API_BASE}/${id}`);
    console.log('✅ [BuyerAPI] Buyer fetched successfully:', response.data.data?.name || response.data.name);
    return response.data;
  } catch (error: any) {
    console.error('❌ [BuyerAPI] Failed to fetch buyer:', error);
    throw error;
  }
}

/**
 * UPDATE BUYER - PUT /api/v1/customers/{id}
 * Updates an existing buyer
 *
 * @param id Buyer ID
 * @param request Update request with fields to update
 * @returns ApiResponse with updated Buyer
 */
export async function updateBuyer(id: number, request: UpdateBuyerRequest): Promise<ApiResponse<Buyer>> {
  try {
    console.log('📡 [BuyerAPI] Updating buyer ID:', id);
    console.log('📡 [BuyerAPI] Update data:', JSON.stringify(request, null, 2));

    const response = await microservicesClient.put<ApiResponse<Buyer>>('commerce-service', `${COMMERCE_API_BASE}/${id}`, request);
    console.log('✅ [BuyerAPI] Buyer updated successfully:', response.data.data || response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ [BuyerAPI] Failed to update buyer:', error);
    throw error;
  }
}

/**
 * CHECK EMAIL UNIQUENESS - GET /api/v1/customers/check-email
 *
 * @param email Email to check
 * @param excludeId Optional buyer ID to exclude from check (for updates)
 * @returns Response with isUnique flag
 */
export async function checkEmailUniqueness(
  email: string,
  excludeId?: number
): Promise<{ isUnique: boolean }> {
  try {
    const params: Record<string, any> = { email };
    if (excludeId) {
      params.excludeId = excludeId;
    }

    const response = await microservicesClient.get<{ isUnique: boolean }>(
      'commerce-service',
      `${COMMERCE_API_BASE}/check-email`,
      { params }
    );
    return response.data;
  } catch (error: any) {
    console.error('❌ [BuyerAPI] Email check failed:', error);
    return { isUnique: true };
  }
}

/**
 * CHECK PHONE UNIQUENESS - GET /api/v1/customers/check-phone
 *
 * @param phone Phone to check
 * @param excludeId Optional buyer ID to exclude from check (for updates)
 * @returns Response with isUnique flag
 */
export async function checkPhoneUniqueness(
  phone: string,
  excludeId?: number
): Promise<{ isUnique: boolean }> {
  try {
    const params: Record<string, any> = { phone };
    if (excludeId) {
      params.excludeId = excludeId;
    }

    const response = await microservicesClient.get<{ isUnique: boolean }>(
      'commerce-service',
      `${COMMERCE_API_BASE}/check-phone`,
      { params }
    );
    return response.data;
  } catch (error: any) {
    console.error('❌ [BuyerAPI] Phone check failed:', error);
    return { isUnique: true };
  }
}

/**
 * CHECK GST UNIQUENESS - GET /api/v1/customers/check-gst
 *
 * @param gst GST number to check
 * @param excludeId Optional buyer ID to exclude from check (for updates)
 * @returns Response with isUnique flag
 */
export async function checkGstUniqueness(
  gst: string,
  excludeId?: number
): Promise<{ isUnique: boolean }> {
  try {
    const params: Record<string, any> = { gst };
    if (excludeId) {
      params.excludeId = excludeId;
    }

    const response = await microservicesClient.get<{ isUnique: boolean }>(
      'commerce-service',
      `${COMMERCE_API_BASE}/check-gst`,
      { params }
    );
    return response.data;
  } catch (error: any) {
    console.error('❌ [BuyerAPI] GST check failed:', error);
    return { isUnique: true };
  }
}
